import { API_BASE, apiFetch } from "../lib/api";
import { HierarchyAccessError, id, object, request, session } from "./api";
import type { TranscriptSelection, TranscriptTurn } from "./transcript";
import { agentProfiles, assignment, currentAgent, pendingAssignment, profile, type AgentChoices, type AgentProfile, type PendingAssignment } from "./agents";
import { finishUpload, pendingUpload, prepareUpload, transferUpload, UploadExpiredError, type PendingUpload } from "./upload";
import { acceptsRepositories, loadRepositoryChoices, loadRepositoryContext, repositoryRequests, sameRepositories, verifiedBindings, type RepositoryChoices, type RepositoryContext, type RunRepository } from "./repositories";

export interface PendingMessage {
  id: string;
  assignmentId: string;
  baseNodeId?: string;
  input: { text: string; artifacts?: InputDocument[] };
  repositories?: RunRepository[];
}
export interface InputDocument { artifact_id: string; version_id: string }
export interface AvailableDocument { reference: InputDocument; title: string }
export interface SendSnapshot {
  scope: string[];
  draft: string;
  documents?: InputDocument[];
  repositories?: RunRepository[];
  pending?: PendingMessage;
  accepted?: { message: PendingMessage; state: string };
  pendingAssignment?: PendingAssignment;
  recoveryAssignment?: PendingAssignment;
  upload?: PendingUpload;
}
export interface SendPersistence {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  withLock<T>(key: string, signal: AbortSignal | undefined, action: () => Promise<T>): Promise<T>;
}

export function browserPersistence(): SendPersistence {
  if (!navigator.locks) throw new Error("This browser cannot safely coordinate message retries.");
  return {
    getItem: (key) => localStorage.getItem(key),
    setItem: (key, value) => localStorage.setItem(key, value),
    withLock: (key, signal, action) => navigator.locks.request(key, { signal }, action),
  };
}

function optionalId(value: unknown): string | undefined {
  return value === undefined || value === "" ? undefined : id(value);
}

function message(raw: unknown): PendingMessage {
  const value = object(raw), input = object(value.input);
  onlyKeys(value, ["id", "assignmentId", "baseNodeId", "input", "repositories"]);
  onlyKeys(input, ["text", "artifacts"]);
  const artifacts = documents(input.artifacts);
  const repositories = repositoryRequests(value.repositories);
  if (typeof input.text !== "string" || (!input.text.trim() && !artifacts.length)) throw new HierarchyAccessError();
  return { id: id(value.id), assignmentId: id(value.assignmentId), baseNodeId: optionalId(value.baseNodeId), input: { text: input.text, ...(artifacts.length ? { artifacts } : {}) }, ...(repositories.length ? { repositories } : {}) };
}

function documents(raw: unknown): InputDocument[] {
  if (raw === undefined || raw === null) return [];
  if (!Array.isArray(raw) || raw.length > 32) throw new HierarchyAccessError();
  const refs = raw.map((item) => {
    const ref = object(item);
    onlyKeys(ref, ["artifact_id", "version_id"]);
    const artifact_id = id(ref.artifact_id), version_id = id(ref.version_id);
    if (artifact_id.length > 256 || version_id.length > 256) throw new HierarchyAccessError();
    return { artifact_id, version_id };
  });
  if (new Set(refs.map((ref) => ref.version_id)).size !== refs.length) throw new HierarchyAccessError();
  return refs;
}

function sameDocuments(a: unknown, b: unknown): boolean {
  return JSON.stringify(documents(a)) === JSON.stringify(documents(b));
}

function onlyKeys(value: Record<string, unknown>, keys: string[]): void {
  if (Object.keys(value).some((key) => !keys.includes(key))) throw new HierarchyAccessError();
}

const states = new Set(["queued", "running", "completed", "failed", "cancelled"]);

export async function openThreadSender(selection: TranscriptSelection, persistence?: SendPersistence, signal?: AbortSignal): Promise<ThreadSender> {
  for (const value of Object.values(selection)) if (value !== undefined) id(value);
  const identity = await session(signal);
  if (identity.accountId !== selection.accountId) throw new HierarchyAccessError();
  const scope = [API_BASE, identity.userId, selection.accountId, selection.workspaceId, selection.projectId ?? "", selection.threadId];
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(JSON.stringify(scope)));
  const key = "chill.thread-send.v1." + Array.from(new Uint8Array(digest), (n) => n.toString(16).padStart(2, "0")).join("");
  const sender = new ThreadSender(selection, scope, key, persistence ?? browserPersistence());
  await sender.read(signal);
  return sender;
}

// Every operation takes the same origin-wide Web Lock and rereads durable state.
// A second tab therefore adopts a pending retry instead of inventing another ID.
export class ThreadSender {
  constructor(private readonly selection: TranscriptSelection, private readonly scope: string[],
    private readonly key: string, private readonly persistence: SendPersistence) {}

  private root(): string {
    return `/code/workspaces/${encodeURIComponent(this.selection.workspaceId)}/threads/${encodeURIComponent(this.selection.threadId)}`;
  }

  private async authorize(signal?: AbortSignal): Promise<Record<string, unknown>> {
    const identity = await session(signal);
    if (identity.accountId !== this.scope[2] || identity.userId !== this.scope[1]) throw new HierarchyAccessError();
    const current = await request(this.root(), signal);
    if (current.id !== this.selection.threadId || current.accountId !== this.selection.accountId || current.workspaceId !== this.selection.workspaceId ||
      optionalId(current.projectId) !== this.selection.projectId) throw new HierarchyAccessError();
    return current;
  }

  private saved(): SendSnapshot {
    const raw = this.persistence.getItem(this.key);
    if (raw === null) return { scope: this.scope, draft: "" };
    const value = object(JSON.parse(raw));
    onlyKeys(value, ["scope", "draft", "documents", "repositories", "pending", "accepted", "pendingAssignment", "recoveryAssignment", "upload"]);
    if (JSON.stringify(value.scope) !== JSON.stringify(this.scope) || typeof value.draft !== "string" || (value.pending !== undefined && value.accepted !== undefined)) throw new Error("Saved message state is unavailable. Sending is paused to avoid a duplicate.");
    const pending = value.pending !== undefined ? message(value.pending) : undefined;
    let accepted: SendSnapshot["accepted"];
    if (value.accepted !== undefined) {
      const receipt = object(value.accepted);
      onlyKeys(receipt, ["message", "state"]);
      if (typeof receipt.state !== "string" || !states.has(receipt.state)) throw new HierarchyAccessError();
      accepted = { message: message(receipt.message), state: receipt.state };
    }
    const choice = value.pendingAssignment === undefined ? undefined : pendingAssignment(value.pendingAssignment);
    if (choice && (pending || accepted)) throw new HierarchyAccessError();
    const recovery = value.recoveryAssignment === undefined ? undefined : pendingAssignment(value.recoveryAssignment);
    if (recovery && (!pending || recovery.previousId !== pending.assignmentId || choice)) throw new HierarchyAccessError();
    const refs = documents(value.documents);
    const repositories = repositoryRequests(value.repositories);
    const upload = value.upload === undefined ? undefined : pendingUpload(value.upload);
    if (upload && (pending || accepted || choice || refs.length >= 32)) throw new HierarchyAccessError();
    return { scope: this.scope, draft: value.draft, ...(refs.length ? { documents: refs } : {}), ...(repositories.length ? { repositories } : {}), pending, accepted, pendingAssignment: choice, recoveryAssignment: recovery, upload };
  }

  private persist(snapshot: SendSnapshot): void {
    this.persistence.setItem(this.key, JSON.stringify(snapshot));
  }

  async read(signal?: AbortSignal): Promise<SendSnapshot> {
    return this.persistence.withLock(this.key, signal, async () => {
      await this.authorize(signal);
      signal?.throwIfAborted();
      return this.saved();
    });
  }

  async saveDraft(text: string, signal?: AbortSignal): Promise<SendSnapshot> {
    return this.persistence.withLock(this.key, signal, async () => {
      // This sender was activated after authorization. Local draft edits remain
      // available offline and never change its fixed account/user storage scope.
      const snapshot = this.saved();
      if (snapshot.pending) throw new Error("Confirm the pending message before editing it.");
      snapshot.draft = text;
      signal?.throwIfAborted();
      this.persist(snapshot);
      return snapshot;
    });
  }

  async saveDocuments(next: InputDocument[], expected: InputDocument[], signal?: AbortSignal): Promise<SendSnapshot> {
    const refs = documents(next);
    return this.persistence.withLock(this.key, signal, async () => {
      const snapshot = this.saved();
      if (snapshot.pending || snapshot.accepted || snapshot.upload) throw new Error("Confirm the pending message or upload before changing its documents.");
      if (!sameDocuments(snapshot.documents, expected)) throw new Error("Documents changed in another window. Reload the saved message.");
      snapshot.documents = refs.length ? refs : undefined;
      signal?.throwIfAborted();
      this.persist(snapshot);
      return snapshot;
    });
  }

  private async authorizeUpload(signal?: AbortSignal): Promise<void> {
    const access = await this.authorize(signal);
    if (access.accessRole !== "admin" && access.accessRole !== "editor") throw new HierarchyAccessError();
  }

  async repositoryChoices(signal?: AbortSignal): Promise<RepositoryChoices> {
    return this.persistence.withLock(this.key, signal, async () => {
      const current = await this.authorize(signal);
      if (current.accessRole !== "admin" && current.accessRole !== "editor") throw new HierarchyAccessError();
      const choices = await loadRepositoryChoices(this.selection, current, signal);
      await this.authorize(signal);
      signal?.throwIfAborted();
      return choices;
    });
  }

  async saveRepositories(next: RunRepository[], expected: RunRepository[], reviewed: RepositoryContext, signal?: AbortSignal): Promise<SendSnapshot> {
    const selected = repositoryRequests(next);
    return this.persistence.withLock(this.key, signal, async () => {
      const current = await this.authorize(signal);
      if (current.accessRole !== "admin" && current.accessRole !== "editor") throw new HierarchyAccessError();
      const snapshot = this.saved();
      if (snapshot.pending || snapshot.accepted || snapshot.upload || snapshot.pendingAssignment) throw new Error("Confirm pending work before changing code access.");
      if (!sameRepositories(snapshot.repositories, expected)) throw new Error("Code access changed in another window. Reload the saved message.");
      const choices = await loadRepositoryChoices(this.selection, current, signal);
      if (JSON.stringify(choices.context) !== JSON.stringify(reviewed) || !acceptsRepositories(choices.context, selected) || selected.some((row) => !choices.repositories.some((resource) => resource.id === row.repositoryId))) throw new Error("Code access changed. Reload before choosing again.");
      await this.authorize(signal);
      signal?.throwIfAborted();
      snapshot.repositories = selected.length ? selected : undefined;
      this.persist(snapshot);
      return snapshot;
    });
  }

  async uploadFile(file: File, signal?: AbortSignal, expectedDocuments: InputDocument[] = []): Promise<SendSnapshot> {
    const deadline = AbortSignal.timeout(120_000);
    signal = signal ? AbortSignal.any([signal, deadline]) : deadline;
    return this.persistence.withLock(this.key, signal, async () => {
      await this.authorizeUpload(signal);
      const snapshot = this.saved();
      if (snapshot.pending || snapshot.accepted || snapshot.pendingAssignment || (snapshot.documents?.length ?? 0) >= 32) throw new Error("Confirm pending work before uploading another file.");
      if (!sameDocuments(snapshot.documents, expectedDocuments)) throw new Error("Documents changed in another window. Reload the saved message.");
      const prepared = await prepareUpload(file, snapshot.upload, signal);
      await this.authorizeUpload(signal);
      snapshot.upload = prepared.pending;
      signal?.throwIfAborted();
      // Persist IDs, scope and source checksum before any upload/completion POST.
      this.persist(snapshot);
      const reference = await transferUpload(this.selection, prepared.pending, prepared.bytes, (bounded) => this.authorizeUpload(bounded), signal);
      signal?.throwIfAborted();
      snapshot.documents = [...(snapshot.documents ?? []), reference];
      snapshot.upload = undefined;
      this.persist(snapshot);
      return snapshot;
    });
  }

  async checkUpload(restartExpired = false, signal?: AbortSignal): Promise<SendSnapshot> {
    const deadline = AbortSignal.timeout(120_000);
    signal = signal ? AbortSignal.any([signal, deadline]) : deadline;
    return this.persistence.withLock(this.key, signal, async () => {
      await this.authorizeUpload(signal);
      const snapshot = this.saved();
      if (!snapshot.upload) return snapshot;
      try {
        const reference = await finishUpload(this.selection, snapshot.upload, signal);
        if (!reference) throw new Error("Choose the original file to resume this upload.");
        await this.authorizeUpload(signal);
        snapshot.documents = [...(snapshot.documents ?? []), reference];
      } catch (error) {
        // Expiry is only trusted from completion: committed receipts replay even
        // after expiry. Never discard an uncertain upload merely by local time.
        if (!restartExpired || !(error instanceof UploadExpiredError)) throw error;
        await this.authorizeUpload(signal);
      }
      signal?.throwIfAborted();
      snapshot.upload = undefined;
      this.persist(snapshot);
      return snapshot;
    });
  }

  async documents(signal?: AbortSignal): Promise<AvailableDocument[]> {
    await this.authorize(signal);
    const root = `/code/workspaces/${encodeURIComponent(this.selection.workspaceId)}`;
    const response = await request(`${root}/artifacts`, signal);
    if (response.artifacts !== null && !Array.isArray(response.artifacts)) throw new HierarchyAccessError();
    const projects = await request(`${root}/projects`, signal);
    if (projects.projects !== null && !Array.isArray(projects.projects)) throw new HierarchyAccessError();
    const shared = new Set<string>();
    const projectIDs = new Set<string>();
    for (const raw of (projects.projects ?? []) as unknown[]) {
      const row = object(raw), projectID = id(row.id);
      if (projectIDs.has(projectID) || row.accountId !== this.selection.accountId || row.workspaceId !== this.selection.workspaceId) throw new HierarchyAccessError();
      projectIDs.add(projectID);
      if (row.visibility === "workspace") shared.add(projectID);
    }
    const seen = new Set<string>();
    const result: AvailableDocument[] = [];
    for (const raw of (response.artifacts ?? []) as unknown[]) {
      const row = object(raw), artifactID = id(row.id), projectID = optionalId(row.projectId);
      if (seen.has(artifactID) || row.accountId !== this.selection.accountId || row.workspaceId !== this.selection.workspaceId || typeof row.title !== "string") throw new HierarchyAccessError();
      seen.add(artifactID);
      // Do not offer a restricted document to a broader conversation. Run
      // admission independently checks current exact-version authority.
      if (projectID && projectID !== this.selection.projectId && !shared.has(projectID)) continue;
      result.push({ title: row.title, reference: { artifact_id: artifactID, version_id: id(row.headVersionId) } });
    }
    await this.authorize(signal);
    signal?.throwIfAborted();
    return result;
  }

  async send(expectedDraft: string, signal?: AbortSignal, expectedAssignmentId?: string, expectedDocuments: InputDocument[] = [], expectedRepositories: RunRepository[] = [], expectedPendingId?: string): Promise<SendSnapshot> {
    return this.persistence.withLock(this.key, signal, async () => {
      const current = await this.authorize(signal);
      if (current.accessRole !== "admin" && current.accessRole !== "editor") throw new HierarchyAccessError();
      const snapshot = this.saved();
      if (snapshot.upload) throw new Error("Confirm the pending upload before sending.");
      if (snapshot.pendingAssignment) throw new Error("Confirm the agent choice before sending.");
      if (snapshot.recoveryAssignment) throw new Error("Finish restoring the saved message before sending.");
      if (expectedPendingId !== undefined && snapshot.pending?.id !== expectedPendingId && snapshot.accepted?.message.id !== expectedPendingId) throw new Error("This message changed in another window. Review it before sending.");
      if (snapshot.accepted) {
        if (snapshot.accepted.message.input.text !== expectedDraft || !sameDocuments(snapshot.accepted.message.input.artifacts, expectedDocuments) || !sameRepositories(snapshot.accepted.message.repositories, expectedRepositories)) throw new Error("Another message was accepted. Review it before continuing.");
        return snapshot;
      }
      if (snapshot.pending && (snapshot.pending.input.text !== expectedDraft || !sameDocuments(snapshot.pending.input.artifacts, expectedDocuments) || !sameRepositories(snapshot.pending.repositories, expectedRepositories))) throw new Error("Another message is pending. Review it before retrying.");
      if (!snapshot.pending) {
        if (snapshot.draft !== expectedDraft || !sameDocuments(snapshot.documents, expectedDocuments) || !sameRepositories(snapshot.repositories, expectedRepositories)) throw new Error("This draft changed in another window. Review it before sending.");
        if (!snapshot.draft.trim() && !snapshot.documents?.length) throw new Error("Enter a message or attach a document first.");
        const assignment = await request(`${this.root()}/assignment`, signal);
        if (expectedAssignmentId !== undefined && assignment.id !== expectedAssignmentId) throw new Error("The agent changed. Review the current choice before sending.");
        const context = await loadRepositoryContext(this.selection, current, signal);
        if (!acceptsRepositories(context, snapshot.repositories)) throw new Error("Choose code access before sending. Earlier work must keep its saved source.");
        snapshot.pending = { id: crypto.randomUUID(), assignmentId: id(assignment.id), baseNodeId: optionalId(current.headNodeId), input: { text: snapshot.draft, ...(snapshot.documents?.length ? { artifacts: snapshot.documents } : {}) }, ...(snapshot.repositories?.length ? { repositories: snapshot.repositories } : {}) };
      }
      // No POST until the exact input, parent, assignment and retry ID are saved.
      signal?.throwIfAborted();
      this.persist(snapshot);
      const response = await apiFetch(`${this.root()}/runs`, {
        method: "POST", credentials: "include", cache: "no-store", signal,
        headers: { "content-type": "application/json" }, body: JSON.stringify(snapshot.pending),
      });
      if (!response.ok) throw new Error("Delivery couldn't be confirmed. Retry the same message.");
      const receipt = object(await response.json());
      const state = this.validateRun(receipt, snapshot.pending);
      await this.authorize(signal);
      signal?.throwIfAborted();
      snapshot.accepted = { message: snapshot.pending, state };
      const bindings = verifiedBindings(receipt.bindings, snapshot.pending.repositories ?? []);
      snapshot.repositories = bindings.length ? bindings : undefined;
      if (snapshot.draft === snapshot.pending.input.text) snapshot.draft = "";
      if (sameDocuments(snapshot.documents, snapshot.pending.input.artifacts)) snapshot.documents = undefined;
      snapshot.pending = undefined;
      this.persist(snapshot);
      return snapshot;
    });
  }

  async agents(signal?: AbortSignal): Promise<AgentChoices> {
    return this.persistence.withLock(this.key, signal, async () => {
      await this.authorize(signal);
      const profiles = await agentProfiles(this.selection.accountId, this.selection.workspaceId, signal);
      const current = await currentAgent(this.root(), signal);
      await this.authorize(signal);
      signal?.throwIfAborted();
      return { profiles, current, pending: this.saved().pendingAssignment };
    });
  }

  async chooseAgent(selected: AgentProfile, expectedPreviousId: string | undefined, signal?: AbortSignal): Promise<SendSnapshot> {
    const choice = profile(selected);
    if (expectedPreviousId !== undefined) id(expectedPreviousId);
    return this.persistence.withLock(this.key, signal, async () => {
      const access = await this.authorize(signal);
      if (access.accessRole !== "admin" && access.accessRole !== "editor") throw new HierarchyAccessError();
      const snapshot = this.saved();
      if (snapshot.pending || snapshot.accepted || snapshot.upload) throw new Error("Confirm the message or upload before changing agents.");
      if (snapshot.pendingAssignment) {
        const pending = snapshot.pendingAssignment;
        if (pending.profileId !== choice.id || pending.profileRevision !== choice.revision || pending.previousId !== expectedPreviousId) throw new Error("Confirm the saved agent choice before changing it.");
      } else {
        const current = await currentAgent(this.root(), signal);
        if (current?.id !== expectedPreviousId) throw new Error("The agent changed. Reload the current choice.");
        if (current?.profile.id === choice.id && current.profile.revision === choice.revision) return snapshot;
        const available = await agentProfiles(this.selection.accountId, this.selection.workspaceId, signal);
        if (!available.some((item) => item.id === choice.id && item.revision === choice.revision)) throw new HierarchyAccessError();
        snapshot.pendingAssignment = { id: crypto.randomUUID(), previousId: current?.id, profileId: choice.id, profileRevision: choice.revision };
      }
      signal?.throwIfAborted();
      this.persist(snapshot);
      const pending = snapshot.pendingAssignment;
      const response = await apiFetch(`${this.root()}/assignment`, {
        method: "POST", credentials: "include", cache: "no-store", signal,
        headers: { "content-type": "application/json" }, body: JSON.stringify(pending),
      });
      if (!response.ok) throw new Error("The agent choice couldn't be confirmed. Retry the saved choice.");
      const receipt = assignment(await response.json());
      if (receipt.id !== pending.id || receipt.previousId !== pending.previousId || receipt.profile.id !== pending.profileId || receipt.profile.revision !== pending.profileRevision) throw new HierarchyAccessError();
      await this.authorize(signal);
      signal?.throwIfAborted();
      snapshot.pendingAssignment = undefined;
      this.persist(snapshot);
      return snapshot;
    });
  }

  // A competing successor makes the old choice obsolete. This never creates a
  // replacement: the person must review the current agent and choose explicitly.
  async useCurrentAgent(signal?: AbortSignal): Promise<SendSnapshot> {
    return this.persistence.withLock(this.key, signal, async () => {
      await this.authorize(signal);
      const snapshot = this.saved(), pending = snapshot.pendingAssignment;
      if (!pending) return snapshot;
      const current = await currentAgent(this.root(), signal);
      if (!current || current.id === pending.previousId) throw new Error("The saved choice is still unconfirmed. Retry it first.");
      if (current.id === pending.id && (current.previousId !== pending.previousId || current.profile.id !== pending.profileId || current.profile.revision !== pending.profileRevision)) throw new HierarchyAccessError();
      await this.authorize(signal);
      signal?.throwIfAborted();
      snapshot.pendingAssignment = undefined;
      this.persist(snapshot);
      return snapshot;
    });
  }

  private validateRun(run: Record<string, unknown>, input: PendingMessage, expectedBindings?: RunRepository[]): string {
    if (run.id !== input.id || run.assignmentId !== input.assignmentId || run.accountId !== this.selection.accountId || run.workspaceId !== this.selection.workspaceId ||
      run.threadId !== this.selection.threadId || optionalId(run.projectId) !== this.selection.projectId || optionalId(run.baseNodeId) !== input.baseNodeId ||
      object(run.input).text !== input.input.text || !sameDocuments(object(run.input).artifacts, input.input.artifacts) || typeof run.state !== "string" || !states.has(run.state)) throw new HierarchyAccessError();
    const bindings = verifiedBindings(run.bindings, input.repositories ?? []);
    if (expectedBindings !== undefined && !sameRepositories(bindings, expectedBindings)) throw new HierarchyAccessError();
    return run.state;
  }

  async refresh(signal?: AbortSignal): Promise<SendSnapshot> {
    return this.persistence.withLock(this.key, signal, async () => {
      await this.authorize(signal);
      const snapshot = this.saved();
      const input = snapshot.accepted?.message ?? snapshot.pending;
      if (!input) return snapshot;
      const receipt = await request(`${this.root()}/runs/${encodeURIComponent(input.id)}`, signal);
      const state = this.validateRun(receipt, input, snapshot.accepted ? snapshot.repositories ?? [] : undefined);
      await this.authorize(signal);
      signal?.throwIfAborted();
      snapshot.accepted = { message: input, state };
      const bindings = verifiedBindings(receipt.bindings, input.repositories ?? []);
      snapshot.repositories = bindings.length ? bindings : undefined;
      snapshot.pending = undefined;
      snapshot.recoveryAssignment = undefined;
      if (snapshot.draft === input.input.text) snapshot.draft = "";
      if (sameDocuments(snapshot.documents, input.input.artifacts)) snapshot.documents = undefined;
      this.persist(snapshot);
      return snapshot;
    });
  }

  async acknowledge(shown: TranscriptTurn[], signal?: AbortSignal): Promise<SendSnapshot> {
    return this.persistence.withLock(this.key, signal, async () => {
      await this.authorize(signal);
      const snapshot = this.saved(), accepted = snapshot.accepted;
      if (!accepted || !["completed", "failed", "cancelled"].includes(accepted.state)) return snapshot;
      const input = accepted.message;
      if (!shown.some((turn) => turn.id === input.id && turn.threadId === this.selection.threadId && turn.accountId === this.selection.accountId && turn.workspaceId === this.selection.workspaceId)) return snapshot;
      const turn = await request(`${this.root()}/turns/${encodeURIComponent(input.id)}`, signal);
      const scope = object(turn.scope);
      if (turn.id !== input.id || scope.run_id !== input.id || scope.thread_id !== this.selection.threadId || scope.account_id !== this.selection.accountId || scope.workspace_id !== this.selection.workspaceId) throw new HierarchyAccessError();
      await this.authorize(signal);
      signal?.throwIfAborted();
      snapshot.accepted = undefined;
      this.persist(snapshot);
      return snapshot;
    });
  }

  async restoreStopped(expectedRunId: string, signal?: AbortSignal): Promise<SendSnapshot> {
    return this.persistence.withLock(this.key, signal, async () => {
      await this.authorize(signal);
      const snapshot = this.saved(), accepted = snapshot.accepted;
      if (!accepted || accepted.message.id !== expectedRunId) throw new HierarchyAccessError();
      const input = accepted.message;
      const path = `${this.root()}/runs/${encodeURIComponent(input.id)}`;
      const stopped = (state: string) => state === "failed" || state === "cancelled";
      if (!stopped(this.validateRun(await request(path, signal), input, snapshot.repositories ?? []))) throw new HierarchyAccessError();
      // Only an explicit not-found after current ancestry authorization permits
      // this recovery. A network error, denial or existing receipt never does.
      const receipt = await apiFetch(`${this.root()}/turns/${encodeURIComponent(input.id)}`, { credentials: "include", cache: "no-store", signal });
      await receipt.body?.cancel();
      if (receipt.status !== 404) throw new HierarchyAccessError();
      if (!stopped(this.validateRun(await request(path, signal), input, snapshot.repositories ?? []))) throw new HierarchyAccessError();
      await this.authorize(signal);
      if (snapshot.draft && snapshot.draft !== input.input.text) throw new Error("Save the current draft before restoring the stopped message.");
      if (snapshot.documents?.length && !sameDocuments(snapshot.documents, input.input.artifacts)) throw new Error("Save the current documents before restoring the stopped message.");
      signal?.throwIfAborted();
      snapshot.draft = input.input.text;
      snapshot.documents = input.input.artifacts;
      snapshot.accepted = undefined;
      this.persist(snapshot);
      return snapshot;
    });
  }

  async restorePending(expectedRunId: string, signal?: AbortSignal): Promise<SendSnapshot> {
    return this.persistence.withLock(this.key, signal, async () => {
      const access = await this.authorize(signal);
      if (access.accessRole !== "admin" && access.accessRole !== "editor") throw new HierarchyAccessError();
      const snapshot = this.saved(), input = snapshot.pending;
      if (!input || input.id !== expectedRunId) throw new HierarchyAccessError();
      const readRun = async () => {
        const response = await apiFetch(`${this.root()}/runs/${encodeURIComponent(input.id)}`, { credentials: "include", cache: "no-store", signal });
        if (response.status === 404) { await response.body?.cancel(); return undefined; }
        if (!response.ok) throw new HierarchyAccessError();
        const receipt = object(await response.json());
        const state = this.validateRun(receipt, input);
        return { receipt, state };
      };
      let found = await readRun();
      if (!found) {
        const current = await currentAgent(this.root(), signal);
        if (!current) throw new HierarchyAccessError();
        if (current.id === input.assignmentId) {
          snapshot.recoveryAssignment ??= { id: crypto.randomUUID(), previousId: current.id, profileId: current.profile.id, profileRevision: current.profile.revision };
          const barrier = snapshot.recoveryAssignment;
          if (barrier.profileId !== current.profile.id || barrier.profileRevision !== current.profile.revision) throw new HierarchyAccessError();
          signal?.throwIfAborted();
          // Save the successor ID before its write; a lost response can replay.
          this.persist(snapshot);
          const response = await apiFetch(`${this.root()}/assignment`, {
            method: "POST", credentials: "include", cache: "no-store", signal,
            headers: { "content-type": "application/json" }, body: JSON.stringify(barrier),
          });
          if (!response.ok) throw new Error("Message recovery couldn't be confirmed. Retry the saved recovery.");
          const receipt = assignment(await response.json());
          if (receipt.id !== barrier.id || receipt.previousId !== barrier.previousId || receipt.profile.id !== barrier.profileId || receipt.profile.revision !== barrier.profileRevision) throw new HierarchyAccessError();
        }
        // Assignment replacement and run admission share the server's thread
        // lock. Once a successor commits, a late request using the old choice
        // can only replay an existing run. A second 404 is therefore conclusive.
        found = await readRun();
      }
      await this.authorize(signal);
      if (found) {
        snapshot.accepted = { message: input, state: found.state };
        const bindings = verifiedBindings(found.receipt.bindings, input.repositories ?? []);
        snapshot.repositories = bindings.length ? bindings : undefined;
        if (snapshot.draft === input.input.text) snapshot.draft = "";
        if (sameDocuments(snapshot.documents, input.input.artifacts)) snapshot.documents = undefined;
      } else {
        if (snapshot.draft !== input.input.text || !sameDocuments(snapshot.documents, input.input.artifacts) || !sameRepositories(snapshot.repositories, input.repositories)) throw new Error("The saved message changed. Reload before restoring it.");
      }
      signal?.throwIfAborted();
      snapshot.pending = undefined;
      snapshot.recoveryAssignment = undefined;
      this.persist(snapshot);
      return snapshot;
    });
  }
}
